import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/RestoreFromTrash";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CircleCheckBigIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleFadingArrowUpIcon,
} from "lucide-react";
import * as React from "react";

import {
  AlertDialog,
  RobotoTableCell,
  RobotoTableRow,
} from "@/shared/components";
import type {
  FeatureFlagRecord,
  RolloutStatus,
} from "@/shared/domain/featureFlags";
import {
  useCreateFeatureFlag,
  useDeleteFeatureFlag,
  useDeletedFeatureFlags,
  useFeatureFlags,
  useRestoreFeatureFlag,
  useUpdateFeatureFlag,
} from "@/shared/domain/featureFlags/hooks";
import { useUserGroups } from "@/shared/domain/userGroups/hooks";

import styles from "./FeatureFlagsPanel.module.css";

interface RolloutOption {
  label: string;
  rolloutStatus: RolloutStatus;
  userGroupId: string | null;
}

const FIXED_ROLLOUT_OPTIONS: RolloutOption[] = [
  { label: "No Users", rolloutStatus: "None", userGroupId: null },
  { label: "All Users", rolloutStatus: "All", userGroupId: null },
];

const explanation =
  "Feature Flags allow us to progressively roll out a change to a subset of our users before " +
  "rolling out to everyone.\n\n" +
  "Feature flag names should be unique. They should not be deleted until after the flag is completely enabled for all " +
  "users (or deprecated and disabled for all users), all references to the flags have been deleted from the code, " +
  "and there is no remaining risk of a rollback restoring the flags into production code.";

const featureFlagOrder: RolloutStatus[] = ["None", "Partial", "All"];
export function FeatureFlagsPanel() {
  const { data: flags = [], isLoading, error } = useFeatureFlags();
  const { data: userGroups = [] } = useUserGroups();
  const createMutation = useCreateFeatureFlag();
  const updateMutation = useUpdateFeatureFlag();
  const deleteMutation = useDeleteFeatureFlag();
  const restoreMutation = useRestoreFeatureFlag();

  const [deletedDialogOpen, setDeletedDialogOpen] = React.useState(false);
  const {
    data: deletedFlags = [],
    isLoading: isLoadingDeleted,
    error: deletedError,
  } = useDeletedFeatureFlags(deletedDialogOpen);
  const [restoreError, setRestoreError] = React.useState<string | null>(null);

  const rolloutOptions: RolloutOption[] = [
    { label: "No Users", rolloutStatus: "None", userGroupId: null },
    ...userGroups.map((ug) => ({
      label: `Group: ${ug.name}`,
      rolloutStatus: "Partial" as RolloutStatus,
      userGroupId: ug.user_group_id,
    })),
    { label: "All Users", rolloutStatus: "All", userGroupId: null },
  ];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingFlag, setEditingFlag] =
    React.useState<FeatureFlagRecord | null>(null);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [selectedRollout, setSelectedRollout] =
    React.useState<RolloutOption | null>(FIXED_ROLLOUT_OPTIONS[0]);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    React.useState<FeatureFlagRecord | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const sortedFlags = React.useMemo(() => {
    return [...flags].sort((a, b) => {
      return (
        featureFlagOrder.indexOf(a.rollout_status) -
        featureFlagOrder.indexOf(b.rollout_status)
      );
    });
  }, [flags]);

  function openCreate() {
    setEditingFlag(null);
    setName("");
    setDescription("");
    setSelectedRollout(FIXED_ROLLOUT_OPTIONS[0]);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(flag: FeatureFlagRecord) {
    setEditingFlag(flag);
    setName(flag.name);
    setDescription(flag.description ?? "");
    const opt =
      flag.rollout_status === "Partial"
        ? rolloutOptions.find(
            (o) => o.userGroupId === flag.partial_rollout_user_group_id,
          ) ?? null
        : rolloutOptions.find(
            (o) => o.rolloutStatus === flag.rollout_status && !o.userGroupId,
          ) ?? null;
    setSelectedRollout(opt);
    setFormError(null);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setFormError(null);
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setFormError("Name is required.");
      return;
    }
    try {
      const updates = {
        name: name.trim(),
        description: description.trim() || null,
        rollout_status: selectedRollout?.rolloutStatus ?? "None",
        partial_rollout_user_group_id: selectedRollout?.userGroupId ?? null,
      };
      if (editingFlag) {
        await updateMutation.mutateAsync({
          featureFlagId: editingFlag.feature_flag_id,
          updates,
        });
      } else {
        await createMutation.mutateAsync(updates);
      }
      closeDialog();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "An error occurred.");
    }
  }

  function rolloutLabel(flag: FeatureFlagRecord): string {
    if (flag.rollout_status === "Partial") {
      const group = userGroups.find(
        (g) => g.user_group_id === flag.partial_rollout_user_group_id,
      );
      return group ? `Group: ${group.name}` : "Partial";
    }
    return flag.rollout_status === "All" ? "All Users" : "No Users";
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const submitLabel = editingFlag ? "Save" : "Create";

  return (
    <div>
      <div className={styles.toolbar}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.explanation}
        >
          {explanation.trim()}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add Feature Flag
        </Button>
      </div>

      {error && (
        <Alert severity="error" className={styles.alert}>
          {error.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>
                <span className={styles.nameCell}>
                  {/* Use an opaque icon to indent the name column the same amount as the table contents. */}
                  <CircleCheckBigIcon
                    fontSize="small"
                    className={styles.statusIcon}
                    opacity={0}
                  />
                  Name
                </span>
              </RobotoTableCell>
              <RobotoTableCell>Description</RobotoTableCell>
              <RobotoTableCell>Rollout</RobotoTableCell>
              <RobotoTableCell>Actions</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <RobotoTableRow>
                <RobotoTableCell colSpan={4} align="center">
                  <CircularProgress size="1.25rem" />
                </RobotoTableCell>
              </RobotoTableRow>
            )}
            {sortedFlags.map((flag) => (
              <FeatureFlagRow
                key={flag.feature_flag_id}
                flag={flag}
                rolloutLabel={rolloutLabel(flag)}
                onEdit={() => openEdit(flag)}
                onDelete={() => {
                  setDeleteError(null);
                  setDeleteTarget(flag);
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        variant="text"
        size="small"
        className={styles.viewDeletedButton}
        onClick={() => {
          setRestoreError(null);
          setDeletedDialogOpen(true);
        }}
      >
        View Deleted Flags
      </Button>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFlag ? "Edit Feature Flag" : "Add Feature Flag"}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            /* the flag should not be renameable after it is created. */
            disabled={editingFlag !== null}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <Autocomplete
            options={rolloutOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.label === b.label}
            value={selectedRollout}
            onChange={(_, v) => setSelectedRollout(v)}
            renderInput={(params) => <TextField {...params} label="Rollout" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size="1rem" /> : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {deleteError && (
        <Alert
          severity="error"
          className={styles.alert}
          onClose={() => setDeleteError(null)}
        >
          {deleteError}
        </Alert>
      )}

      <AlertDialog
        dialogOpen={deleteTarget !== null}
        handleClose={() => setDeleteTarget(null)}
        dialogTitle="Delete Feature Flag"
        dialogText={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"?`}
        dialogAction={async () => {
          if (!deleteTarget) {
            return;
          }
          try {
            await deleteMutation.mutateAsync(deleteTarget.feature_flag_id);
          } catch (err) {
            setDeleteError(
              err instanceof Error
                ? err.message
                : "Failed to delete feature flag.",
            );
          }
        }}
      />

      <Dialog
        open={deletedDialogOpen}
        onClose={() => setDeletedDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Deleted Feature Flags</DialogTitle>
        <DialogContent>
          <p>
            Deleted feature flags will be treated as fully disabled. They should
            not be recycled in code again.
          </p>
          <p>If you deleted a flag by mistake, you can restore it here.</p>
          <br />
          {restoreError && (
            <Alert
              severity="error"
              className={styles.alert}
              onClose={() => setRestoreError(null)}
            >
              {restoreError}
            </Alert>
          )}
          {deletedError && (
            <Alert severity="error">{deletedError.message}</Alert>
          )}
          {isLoadingDeleted && (
            <div className={styles.centered}>
              <CircularProgress size="1.25rem" />
            </div>
          )}
          {!isLoadingDeleted && deletedFlags.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No deleted feature flags.
            </Typography>
          )}
          {deletedFlags.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <RobotoTableCell>Name</RobotoTableCell>
                    <RobotoTableCell>Description</RobotoTableCell>
                    <RobotoTableCell>Last Rollout</RobotoTableCell>
                    <RobotoTableCell>Actions</RobotoTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedFlags.map((flag) => (
                    <RobotoTableRow key={flag.feature_flag_id}>
                      <RobotoTableCell>{flag.name}</RobotoTableCell>
                      <RobotoTableCell>
                        {flag.description ?? "—"}
                      </RobotoTableCell>
                      <RobotoTableCell>{rolloutLabel(flag)}</RobotoTableCell>
                      <RobotoTableCell>
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          disabled={restoreMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              try {
                                await restoreMutation.mutateAsync(
                                  flag.feature_flag_id,
                                );
                              } catch (err) {
                                setRestoreError(
                                  err instanceof Error
                                    ? err.message
                                    : "Failed to restore feature flag.",
                                );
                              }
                            })();
                          }}
                        >
                          Restore
                        </Button>
                      </RobotoTableCell>
                    </RobotoTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

interface FeatureFlagRowProps {
  flag: FeatureFlagRecord;
  rolloutLabel: string;
  onEdit: () => void;
  onDelete: () => void;
}

function FeatureFlagRow({
  flag,
  rolloutLabel,
  onEdit,
  onDelete,
}: FeatureFlagRowProps) {
  const isRolledOut = flag.rollout_status === "All";
  const cellClass = isRolledOut ? styles.deemphasized : undefined;

  let statusIcon: React.ReactNode;
  if (flag.rollout_status === "All") {
    statusIcon = (
      <CircleCheckIcon fontSize="small" className={styles.statusIcon} />
    );
  } else if (flag.rollout_status === "Partial") {
    statusIcon = (
      <CircleFadingArrowUpIcon fontSize="small" className={styles.statusIcon} />
    );
  } else {
    statusIcon = (
      <CircleDashedIcon fontSize="small" className={styles.statusIcon} />
    );
  }

  return (
    <RobotoTableRow>
      {/*<RobotoTableCell className={cellClass}>{statusIcon}</RobotoTableCell>*/}
      <RobotoTableCell className={cellClass}>
        <span className={styles.nameCell}>
          {statusIcon}
          {flag.name}
        </span>
      </RobotoTableCell>
      <RobotoTableCell className={cellClass}>
        {flag.description ?? "—"}
      </RobotoTableCell>
      <RobotoTableCell className={cellClass}>{rolloutLabel}</RobotoTableCell>
      <RobotoTableCell>
        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
          Edit
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
        >
          Delete
        </Button>
      </RobotoTableCell>
    </RobotoTableRow>
  );
}
